import { HierarchyTypeUtilFactory, INodeSelectionCriteria } from '@aon/tws-common-ui-hierarchy';
import { INodeVm } from '@aon/tws-common-ui-hierarchy/build/lib/types';
import _ from 'lodash';
import { Reducer } from 'redux';
import { AppDispatch } from '../appStore';
import { AudienceMode } from '../components/audiences/AudienceMode';
import { AudienceSaveErrorSource } from '../containers/audience/AudienceSaveErrorSource';
import ContentConstants from '../contentConstants';
import { AudiencePropertiesUnderValidation, IDistributionHierarchy, IFileUploadResponse, IValidationErrorCollection } from '../models';
import * as DistributionHierarchyAPI from '../services/DistributionHierarchyAPI';
import { ActionTypes as SharedActionTypes } from '../store/ActionTypes';
import { FileDownloadHelper } from '../utilities';
import { IApplicationState } from './';
import {
	IDistributionAutoSelectHierarchyTopNodes,
	IDistributionHierarchyAccessCodeUploadFailed,
	IDistributionHierarchyAccessCodeUploadSucceeded,
	IDistributionHierarchyGetSucceeded,
	IDistributionHierarchyInitState,
	IDistributionSaveHierarchyFailed,
	IDistributionSaveHierarchyRequested,
	IDistributionSaveHierarchySucceeded,
	IDistributionSaveHierarchyValidationFailed,
	IDistributionSelectHierarchy,
	IDistributionUpdateHierarchySelection,
} from './Actions';
import { ActionTypes } from './ActionTypes';
import { ActionCreators as AudienceActions } from './AudienceDetailsStore';

export const emptyNodeSelection = (): INodeSelectionCriteria => ({
	descendantsIncluded: {},
	singleIncluded: {},
});

export interface IDistributionHierarchyState {
	selectedHierarchyId?: string;
	selectionCriteria: INodeSelectionCriteria;
	distributionHierarchy: IDistributionHierarchy;
	lastSavedDistributionHierarchy: IDistributionHierarchy;
	distributionHierarchySaveInProgress?: boolean;
	accessCodeUploadValidationErrors?: IValidationErrorCollection;
}

export const defaultState: IDistributionHierarchyState = {
	selectedHierarchyId: undefined,
	selectionCriteria: emptyNodeSelection(),
	distributionHierarchy: {
		audienceId: 0,
		externalHrisId: '',
		distributionHierarchyId: 0,
		surveyHierarchyId: 0,
	},
	lastSavedDistributionHierarchy: {
		audienceId: 0,
		externalHrisId: '',
		distributionHierarchyId: 0,
		surveyHierarchyId: 0,
	},
	distributionHierarchySaveInProgress: false,
	accessCodeUploadValidationErrors: undefined,
};

type KnownActions =
	| IDistributionHierarchyGetSucceeded
	| IDistributionSelectHierarchy
	| IDistributionUpdateHierarchySelection
	| IDistributionSaveHierarchyRequested
	| IDistributionSaveHierarchySucceeded
	| IDistributionSaveHierarchyFailed
	| IDistributionAutoSelectHierarchyTopNodes
	| IDistributionHierarchyInitState
	| IDistributionSaveHierarchyValidationFailed
	| IDistributionHierarchyAccessCodeUploadSucceeded
	| IDistributionHierarchyAccessCodeUploadFailed;

export const ActionCreators = {
	GetDistributionHierarchy: (surveyId: number, audienceId: number) => (dispatch: any, getState: () => IApplicationState) => {
		const { audienceSource } = getState().survey.surveyEditModel;

		dispatch({ type: ActionTypes.DISTRIBUTIONHIERARCHY_GET_REQUESTED });
		return DistributionHierarchyAPI.getDistributionHierarchy(surveyId, audienceId)
			.then((distributionHierarchy: IDistributionHierarchy) => {
				if (!distributionHierarchy) {
					distributionHierarchy = {
						audienceId,
						distributionHierarchyId: 0,
						surveyHierarchyId: 0,
						externalHrisId: audienceSource.externalHrisId!,
						externalHierarchyId: audienceSource.externalHierarchyId,
					};
				}
				dispatch({ type: ActionTypes.DISTRIBUTIONHIERARCHY_GET_SUCCEEDED, payload: distributionHierarchy });
			})
			.catch((ex) => {
				dispatch({ type: ActionTypes.DISTRIBUTIONHIERARCHY_GET_FAILED });
			});
	},
	// TODO: add warnings around clearing selections
	SelectHierarchy: (hierarchyId?: string) => ({ type: ActionTypes.DISTRIBUTIONHIERARCHY_SELECT_HIERARCHY, payload: hierarchyId }),
	UpdateHierarchyNodeSelection: (selectionCriteria: INodeSelectionCriteria) => ({
		type: ActionTypes.DISTRIBUTIONHIERARCHY_UPDATE_HIERARCHYSELECTION,
		payload: selectionCriteria,
	}),

	SaveDistributionHierarchy: (surveyId: number, audienceId: number, distributionHierarchy: IDistributionHierarchy) => (dispatch: any) => {
		return new Promise((resolve, reject) => {
			const SaveDistributionHierarchyPromise =
				distributionHierarchy.distributionHierarchyId > 0
					? DistributionHierarchyAPI.updateDistributionHierarchy(surveyId, audienceId, distributionHierarchy)
					: DistributionHierarchyAPI.createDistributionHierarchy(surveyId, audienceId, distributionHierarchy);

			dispatch({ type: ActionTypes.DISTRIBUTIONHIERARCHY_SAVE_REQUESTED });
			return SaveDistributionHierarchyPromise.then((response: IDistributionHierarchy) => {
				dispatch({ type: ActionTypes.DISTRIBUTIONHIERARCHY_SAVE_SUCCEEDED, payload: response });
				resolve(response);
			}).catch((ex) => {
				if (ex.response && ex.response.data && ex.response.data.validationErrors) {
					reject({
						source: AudienceSaveErrorSource.UpdateDistributionHierarchy,
						error: ex,
					});
					return;
				} else {
					dispatch({ type: ActionTypes.DISTRIBUTIONHIERARCHY_SAVE_FAILED });
				}

				dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
				reject(ex);
			});
		});
	},
	GetHierarchyAccessCodeDownload:
		(surveyId: number, audienceId: number, distributionHierarchy: IDistributionHierarchy) => (dispatch: any) => {
			return DistributionHierarchyAPI.getHierarchyDownloadForAccessCodes(surveyId, audienceId, distributionHierarchy)
				.then((response) => {
					FileDownloadHelper.download(response, 'AccessCodeList.xlsx');
				})
				.catch((ex) => {
					dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
				});
		},
	UploadHierarchyAccessCodes: (surveyId: number, audienceId: number, file: File) => (dispatch: AppDispatch) => {
		return new Promise<void>((resolve, reject) => {
			return DistributionHierarchyAPI.uploadHierarchyDownloadForAccessCodes(surveyId, audienceId, file)
				.then((response: IFileUploadResponse) => {
					dispatch({
						type: ActionTypes.DISTRIBUTIONHIERARCHY_ACCESS_CODE_UPLOAD_SUCCEEDED,
						payload: response.validationWarnings,
					});
					dispatch({
						type: ActionTypes.TOAST_SUCCESS_NOTIFICATION,
						payload: ContentConstants.TOAST_UPLOADED_HIERARCHY_CUSTOM_ACCESS_CODES_TEXT,
					});
					resolve();
				})
				.catch((ex) => {
					let validationErrors;
					if (ex.response && ex.response.data && ex.response.data.validationErrors) {
						validationErrors = ex.response.data.validationErrors.validations;
					}
					dispatch({ type: ActionTypes.DISTRIBUTIONHIERARCHY_ACCESS_CODE_UPLOAD_FAILED, payload: validationErrors });
					dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
					reject();
				});
		});
	},
	validateHierarchyAccessCodeSave:
		(
			surveyId: number,
			audienceId: number,
			distributionHierarchy: IDistributionHierarchy,
			mode: AudienceMode,
			advance: boolean,
			selectedHierarchyId?: string,
		) =>
		async (dispatch: any) => {
			if (selectedHierarchyId === null || selectedHierarchyId === undefined) {
				const hierarchyErrors: IValidationErrorCollection = {};
				hierarchyErrors[AudiencePropertiesUnderValidation.Distribution] = [ContentConstants.VALIDATION_HIERARCHY_SELECTION_REQUIRED];
				dispatch({
					type: SharedActionTypes.DISTRIBUTIONHIERARCHY_VALIDATION_FAILED,
					payload: hierarchyErrors,
				});
				return;
			}
			return new Promise<void>((resolve, reject) => {
				return DistributionHierarchyAPI.validateHierarchyCustomAccessCodes(surveyId, audienceId, distributionHierarchy)
					.then(() => {
						dispatch(AudienceActions.SaveAudienceWithHierarchy(mode, advance));
						resolve();
					})
					.catch((ex) => {
						let validationErrors;
						if (ex.response && ex.response.data && ex.response.data.validationErrors) {
							validationErrors = ex.response.data.validationErrors.validations;
						}
						dispatch({ type: ActionTypes.DISTRIBUTIONHIERARCHY_ACCESS_CODE_UPLOAD_FAILED, payload: validationErrors });
						dispatch({
							type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
							payload: {
								message: ContentConstants.TOAST_MISSING_CUSTOM_ACCESS_CODES,
								messageTitle: ContentConstants.TOAST_ERROR_GENERIC_TITLE,
							},
						});
						reject();
					});
			});
		},
	AutoSelectAll: () => (dispatch: any, getState: () => IApplicationState) => {
		const hierarchyVm = getState().Hierarchy;
		return dispatch({ type: ActionTypes.DISTRIBUTION_AUTOSELECT_HIERARCHYTOPNODES, payload: hierarchyVm });
	},
	InitDistributionHierarchyState: () => ({ type: ActionTypes.DISTRIBUTIONHIERARCHY_INIT_STATE }),
};

export const reducer: Reducer<IDistributionHierarchyState, KnownActions> = (
	state: IDistributionHierarchyState = defaultState,
	action: KnownActions,
) => {
	switch (action.type) {
		case ActionTypes.DISTRIBUTIONHIERARCHY_INIT_STATE:
			return { ..._.cloneDeep(defaultState) };
		case ActionTypes.DISTRIBUTIONHIERARCHY_GET_SUCCEEDED: {
			const selectionCriteria: INodeSelectionCriteria = action.payload.hierarchySelection
				? JSON.parse(action.payload.hierarchySelection)
				: { ...defaultState.selectionCriteria };

			return {
				...state,
				distributionHierarchy: { ...action.payload },
				selectionCriteria,
				selectedHierarchyId: action.payload.externalHierarchyId,
			};
		}

		case ActionTypes.DISTRIBUTIONHIERARCHY_SELECT_HIERARCHY: {
			if (action.payload !== state.selectedHierarchyId) {
				return {
					...state,
					selectedHierarchyId: action.payload,
					selectionCriteria: { ...defaultState.selectionCriteria },
					distributionHierarchy: { ...state.distributionHierarchy, hierarchySelection: undefined, externalHierarchyId: action.payload },
				};
			}
			// if the hierarchy hasn't changed, we don't have to do anything
			break;
		}

		case ActionTypes.DISTRIBUTIONHIERARCHY_UPDATE_HIERARCHYSELECTION: {
			return { ...state, selectionCriteria: { ...action.payload } };
		}
		case ActionTypes.DISTRIBUTIONHIERARCHY_SAVE_FAILED:
			return { ...state, distributionHierarchySaveInProgress: false };
		case ActionTypes.DISTRIBUTIONHIERARCHY_SAVE_REQUESTED: {
			return { ...state, distributionHierarchySaveInProgress: true };
		}
		case ActionTypes.DISTRIBUTIONHIERARCHY_SAVE_SUCCEEDED: {
			const selectionCriteria: INodeSelectionCriteria = action.payload.hierarchySelection
				? JSON.parse(action.payload.hierarchySelection)
				: { ...defaultState.selectionCriteria };

			return {
				...state,
				distributionHierarchy: { ...action.payload },
				lastSavedDistributionHierarchy: { ...action.payload },
				selectionCriteria,
				selectedHierarchyId: action.payload.externalHierarchyId,
				distributionHierarchySaveInProgress: false,
			};
		}
		case ActionTypes.DISTRIBUTIONHIERARCHY_VALIDATION_FAILED: {
			return {
				...state,
				distributionHierarchy: { ...state.distributionHierarchy, distributionHierarchyValidationError: action.payload },
			};
		}
		case ActionTypes.DISTRIBUTION_AUTOSELECT_HIERARCHYTOPNODES: {
			const hierarchyVm = action.payload;
			const selectionCriteria: INodeSelectionCriteria = emptyNodeSelection();

			if (hierarchyVm.rootLevelNodes && hierarchyVm.nodes) {
				hierarchyVm.rootLevelNodes.forEach((rootNodeId: string) => {
					const rootNodeVm: INodeVm = hierarchyVm.nodes![rootNodeId];

					const name = HierarchyTypeUtilFactory.getHierarchyTypeUtil(hierarchyVm.hierarchyHeader!.hierarchyType).getNodeDisplayName({
						nodeVm: rootNodeVm,
					});
					selectionCriteria.descendantsIncluded[rootNodeId] = {
						name,
						id: rootNodeId,
						totalRollups: rootNodeVm.totalRollups!,
					};
				});
			}

			return {
				...state,
				selectionCriteria,
			};
		}
		case ActionTypes.DISTRIBUTIONHIERARCHY_ACCESS_CODE_UPLOAD_FAILED:
		case ActionTypes.DISTRIBUTIONHIERARCHY_ACCESS_CODE_UPLOAD_SUCCEEDED: {
			return {
				...state,
				accessCodeUploadValidationErrors: action.payload,
				distributionHierarchy: { ...state.distributionHierarchy, distributionHierarchyValidationError: undefined },
			};
		}
	}
	return state;
};
